import { handleFetchLink } from '../Actions/OrderContextActionsWidget.js'
import { isOrgMemberOfOrder } from '../Actions/OrderActionsChecks.js'
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline/index.js'
import { UserAndOrgWidget } from '../UserAndOrgWidget.js'
import { DateWidget } from '../../../Common/DateWidget.js'
import { getIconFromTaxa } from '../../../../Util/iconUtil.js'
import { ReportDownloadButton } from '../ReportDownloadButton.js'
import { RenderEvenlySpaced } from '../../../../Util/WidgetsUtils.js'
import type { IOrder, ISamplePool, IUser } from '../../../../Model/index.js'
import type { Subject } from 'rxjs'
import { useGetAuthenticatedUser, useGetCompactMode, useOrderNotices } from '../../../../Flux/Selector/index.js'
import { useDispatch } from 'react-redux'
import Actions from '../../../../Flux/index.ts'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.ts'
import { useConfirm } from '../../../Component/ConfirmProvider.js'
import { useModal } from '../../../Modal/UseModal.js'
import { canUseSpinOnOrder } from '../../../../Model/index.js'
import { taxaOptions } from '../../HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import { TargetRegions } from '../../../../Generated/TargetRegion.ts'
import {
  getResultUnitLabel,
  isEdnaProcessingType,
  isOtuProcessingType
} from '../../../../Util/processingType.ts'
import {
  getDetectedSequencingPlatform,
  getEffectiveSequencingPlatform,
  getSampleConfigurationLabel,
  getSequencingPlatformInlineBadgeStyle,
  getSequencingPlatformLabel
} from '../../../../Util/sequencingPlatform.ts'
import { trackOrderAction } from '../../../../Services/Telemetry/index.ts'
import { SequencingPlatformEarlyAccessTag } from '../../../Component/SequencingPlatformEarlyAccessTag.tsx'
import { openNoticesFor } from '../../../../Model/Order/orderNotices.ts'

interface OrderDetailInfoWidgetProps {
  order: IOrder
  message$: Subject<{
    type: 'success' | 'error' | 'warning' | 'info'
    message: string
  }>
  samplePools: ISamplePool[] | 'LOADING'
  tokenChecksum?: string
  otuCount?: number
  setInviteLabModalOpen: (open: boolean) => void
  setClaimLabModalOpen: (open: boolean) => void
}

const safeMessage: (e: unknown) => string | undefined = (e) => {
  if (e && typeof e === 'object' && 'message' in e) {
    const msg = e.message
    if (msg !== undefined && msg !== null) {
      return msg.toString()
    }
  }
  return undefined
}

export const OrderDetailInfoWidget = (props: OrderDetailInfoWidgetProps) => {
  const isCompactMode = useGetCompactMode()
  const user = useGetAuthenticatedUser()
  const notices = useOrderNotices(props.order.id)
  const dispatch = useDispatch()
  const { confirm } = useConfirm()
  const { openModal } = useModal()

  /*
   * These artifacts (Print Summary, OTU.fasta, Summary PDF) are served only by authenticated
   * `/api/orders/*` endpoints — there is no public/checksum path like the Report download has. A
   * share-link viewer who is not a member of the order's org (i.e. hasn't claimed it) would get a
   * 404 when opening them, so we gray them out and nudge them to claim instead of clicking into a
   * dead end. Membership uses the shared isOrgMemberOfOrder predicate — the same one the member-only
   * order actions gate on — so the two can't drift.
   */
  const isOrderMember = isOrgMemberOfOrder(user, props.order)
  const memberOnlyFileTooltip = 'Claim this order to access this file'

  const spinAccess = canUseSpinOnOrder(props.order, user)

  const resolveCustomerButton = (order: IOrder, checksum?: string, user?: IUser) => {
    if (props.order.customer) {
      return (
        <UserAndOrgWidget
          id={props.order.customer}
          org={props.order.customerOrg}
          userId={props.order.draftCreatedAt ? props.order.createdByUser : props.order.claimedByUser}
        />
      )
    }
    if (checksum !== undefined) {
      return (
        <button
          data-testid={'claim-order-btn'}
          className="btn btn-sm btn-primary text-black"
          onClick={async () => {
            if (!user) {
              const dialog = document.getElementById('signup-modal') as HTMLDialogElement
              dialog?.showModal()
            } else if (user.org.id === props.order.lab) {
              trackOrderAction('claim_customer', props.order)
              await confirm(
                {
                  title: 'Claim order of own organization',
                  message:
                    'You are about to claim an order of your own organization. This will prevent other customers of claiming this order. Continue?'
                },
                async () => {
                  dispatch(
                    Actions[orderSlice.name].claimOrderRequest({
                      orderId: order.id,
                      token: checksum
                    })
                  )
                }
              )
            } else {
              trackOrderAction('claim_customer', props.order)
              dispatch(
                Actions[orderSlice.name].claimOrderRequest({
                  orderId: order.id,
                  token: checksum
                })
              )
            }
          }}
        >
          <DocumentDuplicateIcon className="w-4" /> Claim Order
        </button>
      )
    }

    if (user?.org?.id === props.order.lab) {
      return (
        <button
          data-testid={'copy-share-link'}
          className="btn btn-sm btn-primary btn-outline"
          onClick={async () => {
            trackOrderAction('copy_share_link', props.order)
            try {
              await handleFetchLink(props.order.id)
              props.message$.next({ type: 'success', message: 'Share link copied to clipboard!' })
            } catch (e) {
              props.message$.next({
                type: 'error',
                message: safeMessage(e) || 'An error occurred while copying the share link.'
              })
            }
          }}
        >
          <DocumentDuplicateIcon className="w-4" /> copy share link
        </button>
      )
    }
    return <span></span>
  }

  const resolveLabButton = (order: IOrder, checksum?: string, user?: IUser) => {
    if (props.order.lab) {
      return (
        <UserAndOrgWidget
          id={props.order.lab}
          org={props.order.labOrg}
          userId={props.order.draftCreatedAt ? props.order.claimedByUser : props.order.createdByUser}
        />
      )
    }
    if (checksum !== undefined) {
      return (
        <button
          data-testid={'claim-order-btn'}
          className="btn btn-sm btn-primary text-black"
          onClick={async () => {
            if (!user) {
              const dialog = document.getElementById('signup-modal') as HTMLDialogElement
              dialog?.showModal()
            } else if (user.org.id === props.order.lab) {
              trackOrderAction('claim_lab', props.order)
              await confirm(
                {
                  title: 'Claim order of own organization',
                  message:
                    'You are about to claim an order of your own organization. This will prevent other labs of claiming this order. Continue?'
                },
                () => props.setClaimLabModalOpen(true)
              )
            } else {
              trackOrderAction('claim_lab', props.order)
              props.setClaimLabModalOpen(true)
            }
          }}
        >
          <DocumentDuplicateIcon className="w-4" /> Accept Order
        </button>
      )
    }

    if (user?.org?.id === props.order.customer) {
      return (
        <button
          data-testid="invite-lab"
          className="btn btn-sm btn-primary btn-outline"
          onClick={() => {
            trackOrderAction('invite_lab', props.order)
            props.setInviteLabModalOpen(true)
          }}
        >
          Invite Lab
        </button>
      )
    }
    return <span></span>
  }

  const detectedSequencingPlatform = getDetectedSequencingPlatform(props.order, props.samplePools)
  const effectiveSequencingPlatform = getEffectiveSequencingPlatform(detectedSequencingPlatform, props.order.sequencingPlatformOverride)
  const isSequencingPlatformOverridden =
    !!props.order.sequencingPlatformOverride && props.order.sequencingPlatformOverride !== detectedSequencingPlatform

  return (
    <>
      {[
        {
          title: 'Customer',
          value: resolveCustomerButton(props.order, props.tokenChecksum, user)
        },
        {
          title: 'Laboratory',
          value: resolveLabButton(props.order, props.tokenChecksum, user)
        },
        {
          title: 'Order Created at',
          value: props.order.orderCreated && !isCompactMode ? <DateWidget date={props.order.orderCreated} /> : null
        },
        {
          title: 'Sample Configuration',
          value:
            props.order.sampleConfiguration && !isCompactMode
              ? getSampleConfigurationLabel(props.order.sampleConfiguration, effectiveSequencingPlatform)
              : null
        },
        {
          title: 'Sequencing Platform',
          value: effectiveSequencingPlatform && (
            <span
              className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold ${getSequencingPlatformInlineBadgeStyle(effectiveSequencingPlatform)}`}
              title={
                isSequencingPlatformOverridden
                  ? `Detected as ${getSequencingPlatformLabel(detectedSequencingPlatform) ?? 'mixed or unknown'}, overridden by the lab`
                  : undefined
              }
            >
              {getSequencingPlatformLabel(effectiveSequencingPlatform)}
              {isSequencingPlatformOverridden ? ' (overridden)' : ''}
              <SequencingPlatformEarlyAccessTag platform={effectiveSequencingPlatform} />
            </span>
          ),
          /* No answer yet (nothing uploaded, or the samples disagree) hides the row rather than showing a guessed default. */
          exclude: !effectiveSequencingPlatform
        },
        {
          title: 'Allow Singletons',
          value: props.order.allowSingletons === true ? 'true' : props.order.allowSingletons === false ? 'false' : 'unset'
        },
        {
          title: 'Target Taxon',
          value: (
            <span className="flex" key={'taxaValue'}>
              {getIconFromTaxa(props.order.targetTaxonomy, 'w-5 h-5 mr-2')}
              {taxaOptions.find((x) => x.value === props.order.targetTaxonomy)?.label ?? '??'}
              {props.order.targetTaxonomy === 'CO1_ANIMAL' ? (
                <span className={'ml-2'}>{TargetRegions[props.order.targetRegion ?? 'WORLDWIDE'].label}</span>
              ) : (
                <span></span>
              )}
            </span>
          )
        },
        { title: 'Number of Sample Pools', value: props.samplePools?.length || 0 },
        {
          title: 'Total Number of Samples',
          value: (props.samplePools === 'LOADING' ? [] : props.samplePools).map((s) => s.samples?.length)?.reduce((a, b) => a + b, 0) || 0
        },
        { title: `${getResultUnitLabel(props.order.processingType)} Count`, value: props.otuCount ?? '' },
        {
          title: 'Report',
          value: (
            <>
              <div className="flex gap-6">
                <ReportDownloadButton
                  onClick={() => {
                    trackOrderAction('download_report', props.order)
                    dispatch(
                      Actions[orderSlice.name].getReportRequest({
                        orderName: props.order.name,
                        orderId: props.order.id,
                        checksum: props.tokenChecksum
                      })
                    )
                  }}
                  title={'Report'}
                  classNames={'text-primary'}
                  notices={openNoticesFor(notices, 'REPORT')}
                />
                {/*
                  Deliberately a second entry rather than a settings popup on the one above: that one has to stay a
                  single click and has to keep working for the anonymous checksum link, which always serves the
                  plain report.
                */}
                {spinAccess && (
                  <ReportDownloadButton
                    onClick={() => {
                      trackOrderAction('download_report_custom', props.order)
                      openModal('orderReport', { order: props.order })
                    }}
                    title={'Report (options)'}
                    classNames={'text-primary'}
                    notices={openNoticesFor(notices, 'REPORT')}
                  />
                )}
                {isOtuProcessingType(props.order.processingType) && (
                  <>
                    <ReportDownloadButton
                      onClick={() => {
                        trackOrderAction('print_summary', props.order)
                        window.open(`${window.location.origin}/order/${props.order.id}/print-report`, '_blank')
                      }}
                      title={'Print Summary'}
                      classNames={'text-amber-500'}
                      disabled={!isOrderMember}
                      disabledTooltip={memberOnlyFileTooltip}
                    />
                    <ReportDownloadButton
                      onClick={() => {
                        trackOrderAction('download_otu_fasta', props.order)
                        dispatch(Actions[orderSlice.name].downloadOrderArtifactRequest({ orderId: props.order.id, artifact: 'OTU_FASTA' }))
                      }}
                      title={'OTU.fasta'}
                      classNames={'text-purple-600'}
                      disabled={!isOrderMember}
                      disabledTooltip={memberOnlyFileTooltip}
                      notices={openNoticesFor(notices, 'OTU_FASTA')}
                    />
                  </>
                )}
                {isEdnaProcessingType(props.order.processingType) && (
                  <>
                    <ReportDownloadButton
                      onClick={() => {
                        trackOrderAction('download_summary_pdf', props.order)
                        dispatch(
                          Actions[orderSlice.name].downloadOrderArtifactRequest({
                            orderId: props.order.id,
                            artifact: 'PROCESSING_SUMMARY'
                          })
                        )
                      }}
                      title={'Summary PDF'}
                      classNames={'text-purple-600'}
                      disabled={!isOrderMember}
                      disabledTooltip={memberOnlyFileTooltip}
                    />
                  </>
                )}
              </div>
            </>
          ),
          exclude: !props.order.orderDone
        }
      ]
        .filter((x) => x.exclude !== true)
        .filter((x) => !isCompactMode || x.value)
        .map((d) => {
          return <RenderEvenlySpaced text={d.title} value={d.value} key={d.title} />
        })}
    </>
  )
}
